import React, { useEffect } from "react";
import './App.css';
import { Helmet } from "react-helmet";
import { useForm, ValidationError } from "@formspree/react";

const App = () => {
  const [formState, handleSubmit] = useForm("xbdqvezk");

  useEffect(() => {
    const reveals = document.querySelectorAll(".reveal");

    const scrollHandler = () => {
      for (const el of reveals) {
        const windowHeight = window.innerHeight;
        const elementTop = el.getBoundingClientRect().top;
        const elementVisible = 150;

        if (elementTop < windowHeight - elementVisible) {
          el.classList.add("active");
        }
      }
    };

    window.addEventListener("scroll", scrollHandler);
    scrollHandler();

    return () => window.removeEventListener("scroll", scrollHandler);
  }, []);

  return (
    <div className="app">
      <nav className="navbar">
        <div className="navbar-content">
          <div className="logo centered-logo">
            <img src="/logo.png" alt="FirstMark Logo" className="logo-img" />
          </div>
          <ul className="nav-links">
            <li><a href="#about">About</a></li>
            <li><a href="#services">Services</a></li>
            <li><a href="#testimonials">Testimonials</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
      </nav>

      <Helmet>
        <title>FirstMark Media | Story-Driven Content Creation</title>
        <meta name="description" content="FirstMark Media helps creators and brands tell powerful stories through cinematic video, strategic content, and visual direction." />
        <meta property="og:title" content="FirstMark Media" />
        <meta property="og:description" content="Make Your First Mark — with cinematic, strategic content that connects." />
        <meta property="og:image" content="https://www.firstmarkmedia.co.uk/logo.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <section className="intro-section reveal" id="about">
        <header className="intro-content">
          <h1>Make Your First Mark</h1>
          <p>We help forward-thinking businesses and creators launch powerful stories through media, strategy, and direction.</p>
        </header>
      </section>

      <section className="services-section reveal" id="services">
        <h2>Our Services</h2>
        <p className="services-intro">We don’t just create content — we build visual experiences that spark emotion, tell stories, and move audiences. Every frame, photo, and cut is designed with intention.</p>
        <div className="services-list">
          <div className="service-card">
            <h3>Bold Promo Videos</h3>
            <p>We craft cinematic promos that energize your brand — ideal for launches, campaigns, and high-converting website headers.</p>
          </div>
          <div className="service-card">
            <h3>Reels & Short-Form Content</h3>
            <p>Scroll-stopping edits tailored for Instagram, TikTok, and YouTube Shorts. Designed to hook fast, grow your reach, and spark engagement.</p>
          </div>
          <div className="service-card">
            <h3>Brand Storytelling & Filmmaking (Coming Soon)</h3>
            <p>We tell brand stories that resonate. From interviews to narratives, our filmmaking approach adds emotion and clarity to your message.</p>
          </div>
          <div className="service-card">
            <h3>4K Aerial Videography & Photography</h3>
            <p>Capture scale, motion, and drama from the sky. Perfect for real estate, events, and brand showcases.</p>
          </div>
          <div className="service-card">
            <h3>Cinematic Drone Edits</h3>
            <p>Stylized, high-impact aerial edits that transform raw footage into standout visual storytelling.</p>
          </div>
          <div className="service-card">
            <h3>Product Photography That Sells</h3>
            <p>Clean, conversion-focused visuals for products — great for e-commerce and marketing campaigns.</p>
          </div>
          <div className="service-card">
            <h3>Lifestyle & Behind-the-Scenes Branding</h3>
            <p>We capture natural, in-action shots that show your team and brand culture in the most authentic way.</p>
          </div>
        </div>
      </section>

      <section className="section reveal" id="testimonials">
        <h2>Client Testimonials</h2>

        <div className="testimonial">
          <img src="/Yvonne_Ridley.png" alt="Client 1" />
          <div>
            <div className="testimonial-info">
              <span className="testimonial-name">Dr Yvonne Ridley</span>
              <span className="testimonial-role">Journalist and Political Commentator</span>
            </div>
            <div className="testimonial-text">
              <p>Collaborating with FirstMark Media during my 2024 general election campaign was truly transformative. As an independent candidate, I lacked the advantage of substantial party support — making it essential to stand out online.</p>
              <p>They provided clarity, creativity, and strategic insight. Every TikTok, reel, or short-form video was built with intention and purpose.</p>
              <p>One piece went viral and prompted action from local authorities within 72 hours. Their timing, relevance, and community awareness were game-changing.</p>
              <p>We reached 2.5M+ people and gained 3,000+ new followers organically. But it wasn’t just metrics — it was the tone, story, and real connection they helped us build.</p>
              <p>I’d recommend FirstMark Media to anyone looking to create content that’s smart, strategic, and true to their brand.</p>
            </div>
          </div>
        </div>

        <div className="testimonial reverse">
          <img src="/Daanish_Mahmood.jpg" alt="Client 2" />
          <div>
            <div className="testimonial-info">
              <span className="testimonial-name">Daanish Mahmood</span>
              <span className="testimonial-role">Content Creator</span>
            </div>
            <div className="testimonial-text">
              <p>FirstMark Media helped me unite a 750K+ audience under one roof. Their work behind the scenes — from running live events to keeping systems sharp — was next-level.</p>
              <p>They don’t just execute, they lead. They bring structure, vision, and a whole lot of heart to what they do.</p>
              <p>If you want a team that’s dependable, strategic, and easy to work with — I can’t recommend them enough.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="contact-section reveal" id="contact">
        <h2>Get In Touch</h2>
        <p className="contact-intro">Have a project in mind? Tell us about it — we'll get back to you within 1–2 business days.</p>

        {formState.succeeded ? (
          <div className="contact-form contact-form-success" role="status">
            <h3>Thanks — your message is on its way.</h3>
            <p>We'll get back to you within 1–2 business days.</p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit} noValidate>
            <div className="form-row">
              <label htmlFor="name">Name</label>
              <input id="name" type="text" name="name" required autoComplete="name" />
              <ValidationError prefix="Name" field="name" errors={formState.errors} className="form-field-error" />
            </div>

            <div className="form-row">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" name="email" required autoComplete="email" />
              <ValidationError prefix="Email" field="email" errors={formState.errors} className="form-field-error" />
            </div>

            <div className="form-row">
              <label htmlFor="subject">Subject</label>
              <input id="subject" type="text" name="subject" required />
              <ValidationError prefix="Subject" field="subject" errors={formState.errors} className="form-field-error" />
            </div>

            <div className="form-row">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" rows="6" required></textarea>
              <ValidationError prefix="Message" field="message" errors={formState.errors} className="form-field-error" />
            </div>

            {/* Honeypot field for basic spam protection */}
            <input type="text" name="_gotcha" tabIndex="-1" autoComplete="off" style={{ display: "none" }} />

            <button type="submit" className="contact-submit" disabled={formState.submitting}>
              {formState.submitting ? "Sending..." : "Send Enquiry"}
            </button>

            <ValidationError errors={formState.errors} className="form-status form-status-error" />
          </form>
        )}
      </section>

      <footer className="footer reveal">
        <p>
          Prefer email? Reach us at <a href="mailto:contact@firstmarkmedia.co.uk">contact@firstmarkmedia.co.uk</a>
        </p>
        <p>Follow us on Instagram: <a href="https://instagram.com/firstmark.media" target="_blank" rel="noopener noreferrer">@firstmark.media</a></p>
        <p>&copy; {new Date().getFullYear()} FirstMark Media. All rights reserved.</p>
      </footer>
    </div>
  );
};

export default App;
